import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import {
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    IconButton,
    MenuItem,
    Select,
    Slider,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { BusinessSpecification, BusinessSpecificationType } from '../../MicrospecDynamicUI/Models/Business/BusinessSpecification';

export type SpecUpdateFn = (updatedCharacteristics: Record<string, any>) => void;

export interface SpecMapper {
    /** Renders the interactive widget for a specification. */
    widgetMapper: (
        spec: BusinessSpecification,
        onUpdate: SpecUpdateFn,
        accentColor?: string,
    ) => React.ReactNode;
    /** Returns a human-readable display string for the current value. */
    valueMapper: (spec: BusinessSpecification) => string;
    /** Returns the additional price contribution for this spec given a quantity. */
    additionalPriceMapper: (spec: BusinessSpecification, quantity: number) => number;
}

// ─────────────────────────── Helpers ────────────────────────────────────────

function resolveAdditionalPrice(spec: BusinessSpecification): number {
    const ap = spec.additional_price;
    if (typeof ap === 'number') return ap;
    const key = String(spec.type_characteristics['value']);
    return ap[key] ?? 0;
}

// ─────────────────────────── BOOLEAN ────────────────────────────────────────

const booleanMapper: SpecMapper = {
    widgetMapper: (spec, onUpdate) => {
        const tc = { ...spec.type_characteristics };
        const value: boolean = tc['value'] ?? true;
        const disabled: boolean = tc['disabled'] ?? false;
        return (
            <FormControlLabel
                control={
                    <Switch
                        checked={value}
                        disabled={disabled}
                        onChange={e => onUpdate({ ...tc, value: e.target.checked })}
                        size="small"
                    />
                }
                label={value ? 'כן' : 'לא'}
            />
        );
    },
    valueMapper: spec => {
        const value: boolean = spec.type_characteristics['value'] ?? true;
        return value ? 'כן' : 'לא';
    },
    additionalPriceMapper: (spec, quantity) => {
        const value: boolean = spec.type_characteristics['value'] ?? false;
        return value ? resolveAdditionalPrice(spec) * quantity : 0;
    },
};

// ─────────────────────────── NUMBER_RANGE ───────────────────────────────────

interface NumberRangeWidgetProps {
    value: number;
    minValue: number;
    maxValue: number;
    step: number;
    accentColor?: string;
    onUpdate: (next: number) => void;
}

const NumberRangeWidget: React.FC<NumberRangeWidgetProps> = ({ value, minValue, maxValue, step, accentColor, onUpdate }) => {
    const [inputText, setInputText] = React.useState(String(value));
    const [focused, setFocused] = React.useState(false);

    React.useEffect(() => {
        if (!focused) setInputText(String(value));
    }, [value, focused]);

    const commit = (raw: string) => {
        const parsed = parseFloat(raw);
        if (!isNaN(parsed)) {
            const clamped = Math.min(maxValue, Math.max(minValue, parsed));
            onUpdate(clamped);
            setInputText(String(clamped));
        } else {
            setInputText(String(value));
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
                size="small"
                disabled={value <= minValue}
                onClick={() => onUpdate(Math.max(minValue, value - step))}
                sx={{ border: `1px solid ${alpha(accentColor ?? '#2E673E', 0.4)}`, width: 30, height: 30 }}
            >
                <RemoveIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <TextField
                value={inputText}
                size="small"
                onChange={e => setInputText(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => { setFocused(false); commit(inputText); }}
                onKeyDown={e => { if (e.key === 'Enter') commit(inputText); }}
                inputProps={{ style: { textAlign: 'center', fontWeight: 700, padding: '4px 6px', width: 48 } }}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
            />
            <IconButton
                size="small"
                disabled={value >= maxValue}
                onClick={() => onUpdate(Math.min(maxValue, value + step))}
                sx={{ border: `1px solid ${alpha(accentColor ?? '#2E673E', 0.4)}`, width: 30, height: 30 }}
            >
                <AddIcon sx={{ fontSize: 14 }} />
            </IconButton>
        </Box>
    );
};

const numberRangeMapper: SpecMapper = {
    widgetMapper: (spec, onUpdate, accentColor) => {
        const tc = { ...spec.type_characteristics };
        const range: { min?: number; max?: number; step?: number } = tc['range'] ?? {};
        const minValue: number = range.min ?? tc['min'] ?? 0;
        const maxValue: number = range.max ?? tc['max'] ?? 1;
        const step: number = range.step ?? tc['step'] ?? 1;
        const value: number = Math.min(Math.max(tc['value'] ?? minValue, minValue), maxValue);

        return (
            <NumberRangeWidget
                value={value}
                minValue={minValue}
                maxValue={maxValue}
                step={step}
                accentColor={accentColor}
                onUpdate={next => onUpdate({ ...tc, value: next })}
            />
        );
    },
    valueMapper: spec => {
        const value = spec.type_characteristics['value'] ?? 0;
        return String(value);
    },
    additionalPriceMapper: (spec, quantity) => {
        const value: number = spec.type_characteristics['value'] ?? 0;
        const independentOnOrderQuantity: boolean = spec.type_characteristics['independentOnOrderQuantity'] ?? false;
        return independentOnOrderQuantity
            ? value * resolveAdditionalPrice(spec)
            : value * resolveAdditionalPrice(spec) * quantity;
    },
};

// ─────────────────────────── LIST_OF_VALUES ─────────────────────────────────

const listOfValuesMapper: SpecMapper = {
    widgetMapper: (spec, onUpdate, accentColor) => {
        const tc = { ...spec.type_characteristics };
        const values: string[] = tc['values'] ?? [];
        const minIdx = 0;
        const maxIdx = Math.max(0, values.length - 1);
        const value: number = Math.min(Math.max(tc['value'] ?? 0, minIdx), maxIdx);
        const priceMap = typeof spec.additional_price !== 'number' ? spec.additional_price : null;

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {values.map((label, i) => {
                    const selected = value === i;
                    const optionPrice = priceMap ? (priceMap[String(i)] ?? 0) : null;
                    return (
                        <Box
                            key={i}
                            onClick={() => onUpdate({ ...tc, value: i })}
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 2,
                                border: `1.5px solid ${selected ? accentColor : alpha(accentColor ?? '#2E673E', 0.3)}`,
                                backgroundColor: selected ? alpha(accentColor ?? '#2E673E', 0.12) : 'transparent',
                                cursor: 'pointer',
                                fontSize: '0.78rem',
                                fontWeight: selected ? 700 : 400,
                                color: selected ? accentColor : 'text.secondary',
                                transition: 'all 0.15s',
                                userSelect: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            {label}
                            {optionPrice !== null && optionPrice !== 0 && (
                                <Typography component="span" sx={{ fontSize: '0.68rem', opacity: 0.75, fontWeight: 400 }}>
                                    +{optionPrice}₪
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    },
    valueMapper: spec => {
        const values: string[] = spec.type_characteristics['values'] ?? [];
        const idx: number = spec.type_characteristics['value'] ?? 0;
        return values[idx] ?? '';
    },
    additionalPriceMapper: (spec, quantity) => resolveAdditionalPrice(spec) * quantity,
};

// ─────────────────────────── COLOR_PICKER ───────────────────────────────────

const DEFAULT_COLORS = ['#8B4513', '#A0522D', '#D2691E', '#DEB887', '#F5DEB3', '#808080', '#000000', '#FFFFFF'];

const colorPickerMapper: SpecMapper = {
    widgetMapper: (spec, onUpdate, accentColor) => {
        const tc = { ...spec.type_characteristics };
        const colors: string[] = tc['colors'] ?? DEFAULT_COLORS;
        const selected: string = tc['value'] ?? colors[0];

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {colors.map(color => (
                    <Tooltip key={color} title={color} arrow>
                        <Box
                            onClick={() => onUpdate({ ...tc, value: color })}
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: color,
                                cursor: 'pointer',
                                border: selected === color
                                    ? `3px solid ${accentColor ?? '#2E673E'}`
                                    : '2px solid rgba(0,0,0,0.15)',
                                boxShadow: selected === color ? `0 0 0 2px #fff, 0 0 0 4px ${accentColor ?? '#2E673E'}` : 'none',
                                transition: 'all 0.15s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {selected === color && (
                                <CheckIcon sx={{ fontSize: 14, color: isLightColor(color) ? '#333' : '#fff' }} />
                            )}
                        </Box>
                    </Tooltip>
                ))}
            </Box>
        );
    },
    valueMapper: spec => spec.type_characteristics['value'] ?? '#000000',
    additionalPriceMapper: (spec, quantity) => resolveAdditionalPrice(spec) * quantity,
};

function isLightColor(hex: string): boolean {
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 128;
}

// ─────────────────────────── SLIDER ─────────────────────────────────────────

const sliderMapper: SpecMapper = {
    widgetMapper: (spec, onUpdate, accentColor) => {
        const tc = { ...spec.type_characteristics };
        const minValue: number = (tc['min'] ?? 0) as number;
        const maxValue: number = (tc['max'] ?? 100) as number;
        const divisions: number = (tc['divisions'] ?? 10) as number;
        const unit: string = tc['unit'] ?? '';
        const value: number = Math.min(Math.max((tc['value'] ?? minValue) as number, minValue), maxValue);

        return (
            <Box sx={{ px: 1, pt: 1 }}>
                <Slider
                    value={value}
                    min={minValue}
                    max={maxValue}
                    step={(maxValue - minValue) / divisions}
                    marks
                    valueLabelDisplay="auto"
                    valueLabelFormat={v => `${v}${unit}`}
                    onChange={(_e, v) => onUpdate({ ...tc, value: v as number })}
                    sx={{ color: accentColor }}
                    size="small"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                    {value}{unit}
                </Typography>
            </Box>
        );
    },
    valueMapper: spec => {
        const value: number = (spec.type_characteristics['value'] ?? 0) as number;
        const unit: string = spec.type_characteristics['unit'] ?? '';
        return `${value}${unit}`;
    },
    additionalPriceMapper: (spec, quantity) => {
        const value: number = (spec.type_characteristics['value'] ?? 0) as number;
        return value * resolveAdditionalPrice(spec) * quantity;
    },
};

// ─────────────────────────── TEXT_INPUT ─────────────────────────────────────

const textInputMapper: SpecMapper = {
    widgetMapper: (spec, onUpdate) => {
        const tc = { ...spec.type_characteristics };
        const value: string = tc['value'] ?? '';
        const placeholder: string = tc['placeholder'] ?? 'הכנס טקסט...';
        const maxLength: number = tc['maxLength'] ?? 100;
        const multiline: boolean = tc['multiline'] ?? false;

        return (
            <TextField
                value={value}
                placeholder={placeholder}
                inputProps={{ maxLength }}
                multiline={multiline}
                minRows={multiline ? 2 : undefined}
                maxRows={multiline ? 4 : undefined}
                size="small"
                fullWidth
                onChange={e => onUpdate({ ...tc, value: e.target.value })}
            />
        );
    },
    valueMapper: spec => spec.type_characteristics['value'] ?? '',
    additionalPriceMapper: (spec, quantity) => {
        const value: string = spec.type_characteristics['value'] ?? '';
        return value.length > 0 ? resolveAdditionalPrice(spec) * quantity : 0;
    },
};

// ─────────────────────────── DATE_TIME ──────────────────────────────────────

const dateTimeMapper: SpecMapper = {
    widgetMapper: (spec, onUpdate) => {
        const tc = { ...spec.type_characteristics };
        const timestamp: number = tc['value'] ?? Date.now();
        const includeTime: boolean = tc['includeTime'] ?? false;
        const dateObj = new Date(timestamp);
        const localISO = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, includeTime ? 16 : 10);

        return (
            <TextField
                type={includeTime ? 'datetime-local' : 'date'}
                value={localISO}
                size="small"
                onChange={e => {
                    const parsed = Date.parse(e.target.value);
                    if (!isNaN(parsed)) onUpdate({ ...tc, value: parsed });
                }}
            />
        );
    },
    valueMapper: spec => {
        const timestamp: number = spec.type_characteristics['value'] ?? Date.now();
        const includeTime: boolean = spec.type_characteristics['includeTime'] ?? false;
        const d = new Date(timestamp);
        return includeTime ? d.toLocaleString('he-IL') : d.toLocaleDateString('he-IL');
    },
    additionalPriceMapper: (spec, quantity) => resolveAdditionalPrice(spec) * quantity,
};

// ─────────────────────────── SINGLE_SELECT ──────────────────────────────────

const singleSelectMapper: SpecMapper = {
    widgetMapper: (spec, onUpdate) => {
        const tc = { ...spec.type_characteristics };
        const options: string[] = tc['options'] ?? [];
        if (options.length === 0) return null;
        const value: string = tc['value'] ?? options[0];

        return (
            <Select
                value={value}
                size="small"
                onChange={e => onUpdate({ ...tc, value: e.target.value })}
                sx={{ minWidth: 160 }}
            >
                {options.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
            </Select>
        );
    },
    valueMapper: spec => spec.type_characteristics['value']?.toString() ?? '',
    additionalPriceMapper: (spec, quantity) => resolveAdditionalPrice(spec) * quantity,
};

// ─────────────────────────── MULTI_SELECT ───────────────────────────────────

const multiSelectMapper: SpecMapper = {
    widgetMapper: (spec, onUpdate) => {
        const tc = { ...spec.type_characteristics };
        const options: string[] = tc['options'] ?? [];
        const selected: string[] = Array.isArray(tc['value']) ? [...tc['value']] : [];

        const toggle = (opt: string) => {
            const next = selected.includes(opt)
                ? selected.filter(s => s !== opt)
                : [...selected, opt];
            onUpdate({ ...tc, value: next });
        };

        return (
            <FormGroup>
                {options.map(opt => (
                    <FormControlLabel
                        key={opt}
                        label={opt}
                        control={
                            <Checkbox
                                checked={selected.includes(opt)}
                                onChange={() => toggle(opt)}
                                size="small"
                            />
                        }
                    />
                ))}
            </FormGroup>
        );
    },
    valueMapper: spec => {
        const selected: string[] = Array.isArray(spec.type_characteristics['value'])
            ? spec.type_characteristics['value']
            : [];
        return selected.join(', ');
    },
    additionalPriceMapper: (spec, quantity) => {
        const selected: string[] = Array.isArray(spec.type_characteristics['value'])
            ? spec.type_characteristics['value']
            : [];
        return selected.length * resolveAdditionalPrice(spec) * quantity;
    },
};

// ─────────────────────────── IMAGE_UPLOAD ───────────────────────────────────

const imageUploadMapper: SpecMapper = {
    widgetMapper: (spec, onUpdate, accentColor) => {
        const tc = { ...spec.type_characteristics };
        const maxImages: number = tc['maxImages'] ?? 1;
        const imageUrls: string[] = Array.isArray(tc['value']) ? [...tc['value']] : [];

        const handleFiles = (files: FileList | null) => {
            if (!files) return;
            const remaining = maxImages - imageUrls.length;
            const toAdd = Array.from(files).slice(0, remaining);
            Promise.all(
                toAdd.map(
                    file =>
                        new Promise<string>((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = e => resolve(e.target?.result as string);
                            reader.onerror = reject;
                            reader.readAsDataURL(file);
                        }),
                ),
            ).then(dataUrls => {
                onUpdate({ ...tc, value: [...imageUrls, ...dataUrls] });
            });
        };

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {imageUrls.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {imageUrls.map((url, i) => (
                            <Box key={i} sx={{ position: 'relative' }}>
                                <Box
                                    component="img"
                                    src={url}
                                    alt={`תמונה ${i + 1}`}
                                    sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, border: '1px solid rgba(0,0,0,0.15)' }}
                                />
                                <Box
                                    onClick={() => onUpdate({ ...tc, value: imageUrls.filter((_, j) => j !== i) })}
                                    sx={{
                                        position: 'absolute',
                                        top: -6,
                                        right: -6,
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        backgroundColor: 'error.main',
                                        color: '#fff',
                                        fontSize: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        fontWeight: 700,
                                        lineHeight: 1,
                                    }}
                                >
                                    ×
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
                {imageUrls.length < maxImages && (
                    <Box
                        component="label"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.5,
                            border: `1.5px dashed ${alpha(accentColor ?? '#2E673E', 0.5)}`,
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            color: accentColor,
                            '&:hover': { backgroundColor: alpha(accentColor ?? '#2E673E', 0.05) },
                        }}
                    >
                        <AddIcon sx={{ fontSize: 16 }} />
                        העלה תמונה ({imageUrls.length}/{maxImages})
                        <input
                            type="file"
                            accept="image/*"
                            multiple={maxImages > 1}
                            style={{ display: 'none' }}
                            onChange={e => handleFiles(e.target.files)}
                        />
                    </Box>
                )}
            </Box>
        );
    },
    valueMapper: spec => {
        const urls: string[] = Array.isArray(spec.type_characteristics['value'])
            ? spec.type_characteristics['value']
            : [];
        return urls.length === 0 ? 'לא הועלו תמונות' : `${urls.length} תמונות`;
    },
    additionalPriceMapper: (spec, quantity) => {
        const urls: string[] = Array.isArray(spec.type_characteristics['value'])
            ? spec.type_characteristics['value']
            : [];
        return urls.length * resolveAdditionalPrice(spec) * quantity;
    },
};

// ─────────────────────────── Registry ───────────────────────────────────────

export const specificationMappers: Record<BusinessSpecificationType, SpecMapper> = {
    BOOLEAN: booleanMapper,
    NUMBER_RANGE: numberRangeMapper,
    LIST_OF_VALUES: listOfValuesMapper,
    COLOR_PICKER: colorPickerMapper,
    SLIDER: sliderMapper,
    TEXT_INPUT: textInputMapper,
    DATE_TIME: dateTimeMapper,
    SINGLE_SELECT: singleSelectMapper,
    MULTI_SELECT: multiSelectMapper,
    IMAGE_UPLOAD: imageUploadMapper,
};

/** Deep-clone type_characteristics so mutations don't bleed between renders. */
export function cloneSpecifications(specs: BusinessSpecification[]): BusinessSpecification[] {
    return specs.map(s => ({ ...s, type_characteristics: JSON.parse(JSON.stringify(s.type_characteristics)) }));
}
